import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Chip,
  Snackbar,
  Alert,
  InputAdornment,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PaymentIcon from "@mui/icons-material/Payment";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import BugReportIcon from "@mui/icons-material/BugReport";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { createSupport } from "../../../redux/slices/supportSlice";
import { useDispatch } from 'react-redux';

// FAQ data
const FAQ_CATEGORIES = [
  {
    id: "ordering",
    label: "Ordering",
    icon: <ShoppingCartIcon sx={{ fontSize: 16 }} />,
    color: "#2e7d32",
    bg: "#e8f5e9",
    faqs: [
      {
        q: "How do I place an order?",
        a: "Browse the menu, tap ADD on any item, customise if needed, then tap View Cart. Choose your order type (Dine In / Takeaway / Delivery), fill in your details, and tap Place Order — it sends directly to the restaurant via WhatsApp.",
      },
      {
        q: "Can I add special instructions?",
        a: "Yes! For customisable items you can select size, variant, and add-ons. For special requests, mention them in the WhatsApp message after placing the order.",
      },
      {
        q: "How do I change my order after placing it?",
        a: "Contact the restaurant directly on WhatsApp as soon as possible. Once the restaurant confirms your order, changes may not be possible.",
      },
      {
        q: "Can I place a pre-order?",
        a: "Currently we don't support scheduled pre-orders. All orders are sent in real-time to the restaurant.",
      },
    ],
  },
  {
    id: "offers",
    label: "Offers",
    icon: <LocalOfferIcon sx={{ fontSize: 16 }} />,
    color: "#e65100",
    bg: "#fff3e0",
    faqs: [
      {
        q: "How do I apply a promo code?",
        a: "Go to your cart and find the 'Promo Code' section. Enter your code and tap Apply. The discount will be shown in the bill summary automatically.",
      },
      {
        q: "Why is my promo code not working?",
        a: "Codes may be expired, already used, or require a minimum order amount. Check the offer details on the Offers page. If the issue persists, contact support.",
      },
      {
        q: "Are BOGO offers applied automatically?",
        a: "Yes! BOGO (Buy 1 Get 1) and eligible cart offers are auto-applied when you meet the conditions. You'll see the savings reflected in your cart total.",
      },
      {
        q: "Can I use multiple offers together?",
        a: "Only one promo code can be applied per order. However, auto-applied item/category discounts may stack with your promo code depending on restaurant settings.",
      },
    ],
  },
  {
    id: "delivery",
    label: "Delivery",
    icon: <DeliveryDiningIcon sx={{ fontSize: 16 }} />,
    color: "#1565c0",
    bg: "#e3f2fd",
    faqs: [
      {
        q: "What areas do you deliver to?",
        a: "Delivery zones are set by each restaurant. The restaurant will confirm delivery availability when they receive your WhatsApp order.",
      },
      {
        q: "How long does delivery take?",
        a: "Estimated delivery time is 30–50 minutes depending on your location and restaurant load. The restaurant will communicate the exact time on WhatsApp.",
      },
      {
        q: "Is there a minimum order for delivery?",
        a: "Minimum order amounts vary by restaurant. If your order doesn't meet the minimum, the restaurant will inform you.",
      },
      {
        q: "How do I track my delivery?",
        a: "After placing your order, the restaurant will share updates via WhatsApp. You can also call the restaurant directly using the contact details on the Home tab.",
      },
    ],
  },
  {
    id: "payment",
    label: "Payment",
    icon: <PaymentIcon sx={{ fontSize: 16 }} />,
    color: "#7b1fa2",
    bg: "#f3e5f5",
    faqs: [
      {
        q: "What payment methods are accepted?",
        a: "Payment is handled directly with the restaurant — cash, UPI, or card depending on the restaurant's setup. Payment details are discussed on WhatsApp after placing your order.",
      },
      {
        q: "Is my payment info stored in the app?",
        a: "No. This app doesn't process or store any payment information. All payments are handled offline between you and the restaurant.",
      },
      {
        q: "Can I get a refund?",
        a: "Refund policies are set by individual restaurants. Contact the restaurant on WhatsApp to discuss cancellation or refund for your order.",
      },
    ],
  },
  {
    id: "technical",
    label: "Technical",
    icon: <BugReportIcon sx={{ fontSize: 16 }} />,
    color: "#c62828",
    bg: "#ffebee",
    faqs: [
      {
        q: "The menu is not loading — what do I do?",
        a: "Try refreshing the page. Check your internet connection. If the problem continues, clear your browser cache or try a different browser.",
      },
      {
        q: "My cart items disappeared — why?",
        a: "Cart data is stored locally in your browser. Clearing browser data or using a private/incognito window will reset the cart. This is expected behaviour.",
      },
      {
        q: "WhatsApp didn't open after I placed an order?",
        a: "Make sure WhatsApp is installed on your device. If you're on desktop, ensure WhatsApp Web is set up. Try placing the order again — your cart is still saved.",
      },
      {
        q: "The app is slow on my phone — how to fix?",
        a: "Close other browser tabs and apps. Try refreshing the page. For the best experience, use an up-to-date version of Chrome or Safari.",
      },
    ],
  },
];

// ── Contact options
const CONTACT_OPTIONS = [
  {
    icon: <WhatsAppIcon sx={{ fontSize: 22, color: "#25D366" }} />,
    label: "WhatsApp Support",
    desc: "Fastest response · Usually within 1 hour",
    action: "Chat Now",
    color: "#25D366",
    bg: "#f0fff4",
    border: "#b7f5c8",
    onClick: () =>
      window.open(
        "https://wa.me/917734904014?text=Hi, I need help with my order.",
        "_blank",
      ),
  },
  {
    icon: <EmailIcon sx={{ fontSize: 22, color: "#1565c0" }} />,
    label: "Email Support",
    desc: "restaurantwala24@gmail.com · Reply within 24 hrs",
    action: "Send Email",
    color: "#1565c0",
    bg: "#e3f2fd",
    border: "#90caf9",
    onClick: () =>
      window.open(
        "mailto:restaurantwala24@gmail.com?subject=Support Request",
        "_blank",
      ),
  },
  {
    icon: <PhoneIcon sx={{ fontSize: 22, color: "#e65100" }} />,
    label: "Call Us",
    desc: "Mon–Sat, 9 AM – 7 PM",
    action: "Call Now",
    color: "#e65100",
    bg: "#fff3e0",
    border: "#ffcc80",
    onClick: () => window.open("tel:+919462973435"),
  },
];

const HelpSupportPage = ({ onBack, }) => {
const { restaurantId } = useParams();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [expanded, setExpanded] = useState(false);
  const [showTicket, setShowTicket] = useState(false);
  const [ticket, setTicket] = useState({ name: "", phone: "", email: "", issue: "" });
  const [ticketSent, setTicketSent] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  // ── Filtered FAQs
  const filteredFAQs = useMemo(() => {
    const cats =
      activeCategory === "all"
        ? FAQ_CATEGORIES
        : FAQ_CATEGORIES.filter((c) => c.id === activeCategory);
    if (!searchQuery.trim()) return cats;
    const q = searchQuery.toLowerCase();
    return cats
      .map((cat) => ({
        ...cat,
        faqs: cat.faqs.filter(
          (f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q),
        ),
      }))
      .filter((cat) => cat.faqs.length > 0);
  }, [searchQuery, activeCategory]);

  const totalResults = filteredFAQs.reduce((n, c) => n + c.faqs.length, 0);

//   // ── Submit ticket
//   const handleTicketSubmit = () => {
//     if (!ticket.name.trim() || !ticket.email.trim() || !ticket.issue.trim()) {
//       setToast({
//         open: true,
//         msg: "Please fill all fields",
//         severity: "warning",
//       });
//       return;
//     }
//     setTicketSent(true);
//     setTicket({ name: "", email: "", issue: "" });
//     setToast({
//       open: true,
//       msg: "Support ticket submitted! We'll get back to you soon 📬",
//       severity: "success",
//     });

//     dispatch(createSupport({
//    restaurantId,
//    name,
//    email,
//    phone,
//    message
// }))
//   };

// ── Submit ticket
const handleTicketSubmit = () => {

  if (!ticket.name.trim() || !ticket.email.trim() || !ticket.issue.trim()) {
    setToast({
      open: true,
      msg: "Please fill all fields",
      severity: "warning",
    });
    return;
  }

  dispatch(createSupport({
    restaurantId: restaurantId,
    name: ticket.name,
    email: ticket.email,
    phone: ticket.phone || "",
    message: ticket.issue
  }));

  setTicketSent(true);

  setTicket({
    name: "",
    email: "",
    phone: "",
    issue: ""
  });

  setToast({
    open: true,
    msg: "Support ticket submitted! We'll get back to you soon 📬",
    severity: "success",
  });
};

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh", pb: 3 }}>
      {/* Top bar handled by RestaurantLayout */}

      {/* ── Search ── */}
      <Box sx={{ mx: 2, mt: 2.5, mb: 2, position: "relative", zIndex: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search FAQs…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#999", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            bgcolor: "white",
            borderRadius: 2.5,
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
              fontSize: "0.88rem",
              "& fieldset": { border: "none" },
              "&:hover fieldset": { border: "none" },
              "&.Mui-focused fieldset": { border: "1.5px solid #2e7d32" },
            },
          }}
        />
      </Box>

      {/* ── Category Filter Chips ── */}
      <Box
        sx={{
          px: 2,
          mb: 2,
          display: "flex",
          gap: 0.8,
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {[
          { id: "all", label: "All Topics", color: "#2e7d32", bg: "#e8f5e9" },
          ...FAQ_CATEGORIES.map((c) => ({
            id: c.id,
            label: c.label,
            color: c.color,
            bg: c.bg,
          })),
        ].map((cat) => (
          <Chip
            key={cat.id}
            label={cat.label}
            size="small"
            onClick={() => setActiveCategory(cat.id)}
            sx={{
              flexShrink: 0,
              height: 30,
              fontSize: "0.75rem",
              fontWeight: 600,
              bgcolor: activeCategory === cat.id ? cat.color : "white",
              color: activeCategory === cat.id ? "white" : "#555",
              border: activeCategory === cat.id ? "none" : "1px solid #e0e0e0",
              transition: "all 0.15s",
              "&:hover": {
                bgcolor: activeCategory === cat.id ? cat.color : cat.bg,
              },
            }}
          />
        ))}
      </Box>

      {/* ── FAQ Count ── */}
      {searchQuery && (
        <Typography
          variant="caption"
          sx={{ px: 2, display: "block", color: "#888", mb: 1.5 }}
        >
          {totalResults} result{totalResults !== 1 ? "s" : ""} for "
          {searchQuery}"
        </Typography>
      )}

      {/* ── FAQ Sections ── */}
      <Box sx={{ px: 2 }}>
        {filteredFAQs.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              bgcolor: "white",
              borderRadius: 3,
              border: "1px solid #f0f0f0",
            }}
          >
            <Typography sx={{ fontSize: "2.5rem", mb: 1 }}>🔍</Typography>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: "#333" }}
            >
              No results found
            </Typography>
            <Typography variant="caption" sx={{ color: "#888" }}>
              Try different keywords or contact support below
            </Typography>
          </Box>
        )}

        {filteredFAQs.map((cat) => (
          <Box key={cat.id} sx={{ mb: 2.5 }}>
            {/* Section header */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  bgcolor: cat.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: cat.color,
                }}
              >
                {cat.icon}
              </Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, fontSize: "0.88rem", color: "#1a1a1a" }}
              >
                {cat.label}
              </Typography>
              <Chip
                label={cat.faqs.length}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.6rem",
                  bgcolor: cat.bg,
                  color: cat.color,
                  fontWeight: 700,
                }}
              />
            </Box>

            {/* Accordions */}
            <Box
              sx={{
                borderRadius: 2.5,
                overflow: "hidden",
                border: "1px solid #f0f0f0",
                bgcolor: "white",
              }}
            >
              {cat.faqs.map((faq, i) => (
                <Accordion
                  key={i}
                  expanded={expanded === `${cat.id}-${i}`}
                  onChange={(_, open) =>
                    setExpanded(open ? `${cat.id}-${i}` : false)
                  }
                  elevation={0}
                  disableGutters
                  sx={{
                    "&:before": { display: "none" },
                    borderBottom:
                      i < cat.faqs.length - 1 ? "1px solid #f5f5f5" : "none",
                    "&.Mui-expanded": { bgcolor: `${cat.bg}50` },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ fontSize: 18, color: cat.color }} />
                    }
                    sx={{
                      px: 2,
                      py: 0.5,
                      minHeight: "unset",
                      "& .MuiAccordionSummary-content": { my: 1.2 },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: expanded === `${cat.id}-${i}` ? 700 : 500,
                        fontSize: "0.83rem",
                        color: "#1a1a1a",
                        lineHeight: 1.4,
                      }}
                    >
                      {faq.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.8 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#555",
                        fontSize: "0.8rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {faq.a}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      {/* ── Contact Options ── */}
      <Box sx={{ px: 2, mb: 2.5 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, fontSize: "0.95rem", mb: 1.2 }}
        >
          📞 Still need help?
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {CONTACT_OPTIONS.map((opt, i) => (
            <Box
              key={i}
              onClick={opt.onClick}
              sx={{
                p: 1.5,
                bgcolor: opt.bg,
                borderRadius: 2.5,
                border: `1px solid ${opt.border}`,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                cursor: "pointer",
                "&:active": { opacity: 0.8 },
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  bgcolor: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                }}
              >
                {opt.icon}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.83rem",
                    color: "#1a1a1a",
                  }}
                >
                  {opt.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#666", fontSize: "0.7rem" }}
                >
                  {opt.desc}
                </Typography>
              </Box>
              <Chip
                label={opt.action}
                size="small"
                sx={{
                  height: 24,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  bgcolor: opt.color,
                  color: "white",
                  flexShrink: 0,
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Raise a Ticket ── */}
      <Box sx={{ px: 2 }}>
        <Box
          onClick={() => !ticketSent && setShowTicket(!showTicket)}
          sx={{
            p: 2,
            bgcolor: "white",
            borderRadius: 2.5,
            border: "1px solid #f0f0f0",
            cursor: ticketSent ? "default" : "pointer",
            "&:hover": ticketSent ? {} : { borderColor: "#2e7d32" },
            transition: "all 0.2s",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {ticketSent ? (
                <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 20 }} />
              ) : (
                <BugReportIcon sx={{ color: "#888", fontSize: 20 }} />
              )}
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    color: ticketSent ? "#2e7d32" : "#1a1a1a",
                  }}
                >
                  {ticketSent ? "Ticket Submitted!" : "Raise a Support Ticket"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#888", fontSize: "0.7rem" }}
                >
                  {ticketSent
                    ? "We'll respond within 24 hours"
                    : "Describe your issue — we'll get back to you"}
                </Typography>
              </Box>
            </Box>
            {!ticketSent &&
              (showTicket ? (
                <ExpandLessIcon sx={{ color: "#888", fontSize: 20 }} />
              ) : (
                <ExpandMoreIcon sx={{ color: "#888", fontSize: 20 }} />
              ))}
          </Box>

          <Collapse in={showTicket && !ticketSent}>
            <Box
              sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              <TextField
                fullWidth
                size="small"
                label="Your Name *"
                value={ticket.name}
                onChange={(e) => setTicket({ ...ticket, name: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&.Mui-focused fieldset": { borderColor: "#2e7d32" },
                  },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Your Phone *"
                value={ticket.phone}
                onChange={(e) => setTicket({ ...ticket, phone: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&.Mui-focused fieldset": { borderColor: "#2e7d32" },
                  },
                }}
              />

              <TextField
                fullWidth
                size="small"
                label="Email Address *"
                type="email"
                value={ticket.email}
                onChange={(e) =>
                  setTicket({ ...ticket, email: e.target.value })
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&.Mui-focused fieldset": { borderColor: "#2e7d32" },
                  },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Describe your issue *"
                multiline
                rows={3}
                value={ticket.issue}
                onChange={(e) =>
                  setTicket({ ...ticket, issue: e.target.value })
                }
                placeholder="Tell us what happened…"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&.Mui-focused fieldset": { borderColor: "#2e7d32" },
                  },
                }}
              />
              <Button
                variant="contained"
                endIcon={<SendIcon />}
                onClick={handleTicketSubmit}
                sx={{
                  borderRadius: 2,
                  bgcolor: "#2e7d32",
                  "&:hover": { bgcolor: "#1b5e20" },
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Submit Ticket
              </Button>
            </Box>
          </Collapse>
        </Box>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: 70 }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: 2, fontSize: "0.8rem" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HelpSupportPage;
